//TODO: This file along with its slice has to be moved into some folder specific to orderSlice
import { v4 as uuidv4 } from 'uuid';
import { RootState } from '../app/store';
import {
  CartItemAdditionTypes,
  CheckResponseTypes,
  StatusResponseTypes,
} from '../constants/event';
import {
  CheckTransmissionMessage,
  IStatusTransmissionMessage,
  messagingActions,
} from './messagingSlice';
import {
  LoyaltyResponseStatus,
  OrderStatus,
  SLA_THRESHOLD_MS,
  TransactionStatus,
} from './orderSlice.constants';
import { IOrderState, ISendItemProperties } from './orderSlice.props';
import logger from '../utils/logger';
import {
  IBrandMenuOrderItem,
  IMenuUnificationOrderResponse,
} from '../types/brandMenu';
import { buildUnifiedMenuModuleOrder } from '../utils/menuUnification';
import { CartItem } from '../utils/cart';
import { GenericMap } from '../utils/types';
import { InfoTransmissionMessage } from './messagingSlice';
import { Coupon, ICartLoyalty, LoyaltyCouponItem } from './cartSlice.props';
import { Dispatch } from '@reduxjs/toolkit';

const initialOrderState: IOrderState = {
  currentSessionId: null,
  seqId: 0,
  total: '',
  subtotal: '',
  tax: '',
  orderError: '',
  currentTransactionId: null,
  transactions: {},
  currentTransactionItems: {},
  completeClickCount: {},
  canAcceptHypothesisEvents: true,
  requestsById: {},
  requestsOrder: [],
  isPosmonReconnected: false,
  isPosmonDisconnected: false,
  currentTransactionCoupons: {},
  menuUnificationOrderItems: [],
  checkEventItems: [],
  currentTransactionLoyalties: {},
  menuModuleRequestSeqId: 0,
  customerName: '',
};

const processOrderStatusAndCheckResponses = (
  state: IOrderState,
  requestId: string,
  FEATURE_FLAG_FINAL_ORDER_VALIDATION: boolean,
  FEATURE_FLAG_LOYALTY_SUPPORT: boolean
) => {
  const {
    orderStatusResponse,
    checkResponse,
    cartItemIds,
    isFinal,
    couponIds,
    loyalties,
  } = state.requestsById[requestId];

  //order_status response destructuring
  const { data: orderStatusData } =
    (orderStatusResponse as IStatusTransmissionMessage) || {};
  const { status: orderStatus, result } = orderStatusData || {};
  const { transaction_id: orderTransactionId = '' } = result || {};
  const { type = '', message: statusMessage = '' } = orderStatus || {};

  //check response destructuring
  const { data: checkData } = (checkResponse as CheckTransmissionMessage) || {};
  const {
    status: checkStatus = CheckResponseTypes.unknown,
    check,
    transaction_id: checkTransactionId = '',
    coupon_apply_status,
  } = checkData || {};
  const { total = '', subtotal = '', tax = '' } = check || {};

  const transactionId = orderTransactionId || checkTransactionId;

  state.requestsById[requestId].transactionId = transactionId;

  if (
    state.requestsOrder.length &&
    state.requestsOrder[state.requestsOrder.length - 1] === requestId
  )
    state.currentTransactionId = transactionId; // Note: when order_status returns error, no transactionId will be created. currentTransactionId will be set to empty string ''

  if (orderStatusResponse && !checkResponse) {
    //order_status event is received while check event is not received
    state.requestsById[requestId].status = type;
    if (type === StatusResponseTypes.success) {
      state.orderError = '';
      state.requestsById[requestId].transactionId = transactionId;

      state.transactions[transactionId] = {
        data: null,
        status: TransactionStatus.pending, // transactionId is created but not finished
        cartItemIds,
        isFinal,
        couponIds,
        loyalties,
      };

      if (!isFinal) {
        // currentTransactionItems will be reset to {} when Finish Order is clicked.
        // Only the transaction level status is needed to indicate the status of the final order in the dollar sign visual indicator.
        for (let cartItemId of cartItemIds) {
          if (
            state.currentTransactionItems[cartItemId] !== OrderStatus.completed
          ) {
            state.currentTransactionItems[cartItemId] = OrderStatus.pending;
          }
        }

        // Change the coupon apply status to pending
        couponIds.forEach((id) => {
          if (state.currentTransactionCoupons[id] !== OrderStatus.completed) {
            state.currentTransactionCoupons[id] = OrderStatus.pending;
          }
        });

        if (FEATURE_FLAG_LOYALTY_SUPPORT) {
          // Change the loyalty apply status to pending
          loyalties?.forEach(({ code }) => {
            if (
              state.currentTransactionLoyalties[code] !==
              LoyaltyResponseStatus.valid
            ) {
              state.currentTransactionLoyalties[code] =
                LoyaltyResponseStatus.pending;
            }
          });
        }
      }
    } else if (type === StatusResponseTypes.error) {
      state.orderError = statusMessage;
      if (
        !state.errorCartItemId ||
        !cartItemIds.find((id: number) => id === state.errorCartItemId)
      ) {
        state.errorCartItemId = cartItemIds[cartItemIds.length - 1];
      }
      if (state.errorCartItemId) {
        state.currentTransactionItems[state.errorCartItemId] =
          OrderStatus.failed;
      }
      if (isFinal && FEATURE_FLAG_FINAL_ORDER_VALIDATION) {
        state.finalOrderStatus = OrderStatus.failed;
        logger.log({
          message: `Final order failed: ${orderStatusResponse.session_id}`,
          event: orderStatusResponse,
        });
      }
    }
  } else {
    //One of the below two scenarios:
    //(1) order_status event is received and check event is also received
    //(2) order_status event is not received but check event is received

    state.orderError = '';
    state.requestsById[requestId].status = checkStatus;
    state.transactions[transactionId] = {
      data: checkData,
      status:
        checkStatus === CheckResponseTypes.ok
          ? TransactionStatus.completed
          : TransactionStatus.error,
      cartItemIds,
      isFinal,
      couponIds,
      loyalties,
    };

    if (!isFinal) {
      // currentTransactionItems will be reset to {} when Finish Order is clicked.
      // Only the transaction level status is needed to indicate the status of the final order in the dollar sign visual indicator.
      for (let cartItemId of state.transactions[transactionId].cartItemIds) {
        if (
          !state.currentTransactionItems[cartItemId] ||
          state.currentTransactionItems[cartItemId] !== OrderStatus.completed
        ) {
          state.currentTransactionItems[cartItemId] =
            checkStatus === CheckResponseTypes.ok
              ? OrderStatus.completed
              : OrderStatus.failed;
        }
      }
    }

    if (FEATURE_FLAG_LOYALTY_SUPPORT) {
      // Change the loyalty apply status to actual check response
      loyalties?.forEach(({ code }) => {
        state.currentTransactionLoyalties[code] =
          checkStatus === CheckResponseTypes.ok
            ? LoyaltyResponseStatus.valid
            : LoyaltyResponseStatus.invalid;
      });
    }

    if (checkStatus === CheckResponseTypes.ok) {
      if (state.errorCartItemId) {
        state.currentTransactionItems[state.errorCartItemId] =
          OrderStatus.completed; // Set the status of previous error cart item to completed if the following transaction succeeds
      }
      state.errorCartItemId = undefined;

      state.total = total || state.total;
      state.subtotal = subtotal || state.subtotal;
      state.tax = tax || state.tax;

      // POSMON will only send coupon_apply_status if the order is sent successfully
      if (coupon_apply_status && !isFinal) {
        couponIds.forEach((id) => {
          const couponApplyStatus = coupon_apply_status.find(
            (couponApplyStatus) => couponApplyStatus.id === id
          );

          state.currentTransactionCoupons[id] = couponApplyStatus?.applied
            ? OrderStatus.completed
            : OrderStatus.pending;
        });
      }
      if (isFinal && FEATURE_FLAG_FINAL_ORDER_VALIDATION) {
        state.finalOrderStatus = OrderStatus.completed;
        logger.log({
          message: `Final order succeed: ${checkResponse.session_id}`,
          event: checkResponse,
        });
      }
    } else {
      if (
        !state.errorCartItemId ||
        !cartItemIds.find((id) => id === state.errorCartItemId)
      ) {
        if (
          !FEATURE_FLAG_LOYALTY_SUPPORT ||
          (FEATURE_FLAG_LOYALTY_SUPPORT &&
            checkData.error &&
            checkData.error !== LoyaltyResponseStatus.invalid)
        )
          state.errorCartItemId = cartItemIds[cartItemIds.length - 1]; // Set the last cart item id to be the errorCartItemId
      }
      if (state.errorCartItemId) {
        state.currentTransactionItems[state.errorCartItemId] =
          OrderStatus.failed;
      }
      state.orderError = checkData.error ? checkData.error : '';

      if (isFinal && FEATURE_FLAG_FINAL_ORDER_VALIDATION) {
        state.finalOrderStatus = OrderStatus.failed;
        logger.log({
          message: `Final order failed: ${checkResponse.session_id}`,
          event: checkResponse,
        });
      }
    }
  }
};

const setLastOrderItemsFailed = (state: IOrderState, errorMessage: string) => {
  const orderRequestsLength = state.requestsOrder.length;
  if (orderRequestsLength) {
    state.orderError = errorMessage;
    const lastRequestId = state.requestsOrder[orderRequestsLength - 1] || '';
    const cartItemIds = state.requestsById[lastRequestId].cartItemIds;
    if (
      !state.errorCartItemId ||
      !cartItemIds.find((id: number) => id === state.errorCartItemId)
    ) {
      state.errorCartItemId = cartItemIds[cartItemIds.length - 1];
    }
    if (state.errorCartItemId) {
      state.currentTransactionItems[state.errorCartItemId] = OrderStatus.failed;
    }
  }
};

const extractOrderId = (rootState: RootState) => {
  const {
    order: { currentSessionId: hitlOrderId },
    sessionBoundary: {
      orderMessageOrderId: aiOrderId,
      sessionId: outerSessionId,
    },
  } = rootState;
  /**
   * orderId is populated as follows:
   *
   * aiOrderId      -> ID received via AI "order" message       (AI can processes only first order, multiple orders can't be processed by AI)
   * hitlOrderId    -> ID of the last order sent from HITL      (First order: First item request it will be space, other item requests it will be outerSessionId)
   *                                                            (When Cancel/Finish order is clicked, this value will be populated with new uuid)
   * outerSessionId -> ID of the session when car enter happens (This will be same until car exit happens)
   * uuid           -> New UUID                                 (This value would never be used because outerSessionId will always have a value)
   */
  const orderId = aiOrderId || hitlOrderId || outerSessionId || uuidv4();
  logger.log({
    message: `Use order id: ${orderId}`,
    moreInfo: JSON.stringify({
      aiOrderId: aiOrderId,
      hitlOrderId: hitlOrderId,
      outerSessionId: outerSessionId,
    }),
  });
  return orderId;
};

const populateMUOrderItems = (
  data: IMenuUnificationOrderResponse[]
): ISendItemProperties[] => {
  return data.map((item) => ({
    ...item,
    id: -1,
    price: '0', // Hardcoded as price is required in order event by POSLINK
    options: populateMUOrderItems(item.children),
    children: populateMUOrderItems(item.children),
    added_by: CartItemAdditionTypes.human,
  })) as ISendItemProperties[];
};

const buildMenuModuleItems = (
  validCartItems: GenericMap<CartItem>,
  itemsQuantity: Record<string, number>
) => {
  const menuModuleItems: IBrandMenuOrderItem[] = [];
  const menuModuleItemsCartItemIds: number[] = [];

  Object.values(validCartItems).forEach((item) => {
    const menuModuleResult = buildUnifiedMenuModuleOrder(item, itemsQuantity);
    if (menuModuleResult) {
      menuModuleItems.push({
        ...menuModuleResult,
        ...(item.isAutoCombined ? { is_auto_combined: true } : {}),
      });
      menuModuleItemsCartItemIds.push(item.cartItemId);
    }
  });

  return { menuModuleItems, menuModuleItemsCartItemIds };
};

interface IOrderMetadata {
  loyalties?: Array<{ code: string }>;
  customer_info?: { name: string };
  couponno?: string;
  coupons?: any[];
}

interface IBuildOrderMetadataParams {
  FEATURE_FLAG_LOYALTY_SUPPORT: boolean;
  cartLoyalties: ICartLoyalty[];
  customerName?: string;
  sbSessionId: string;
  loyaltyCouponItem?: LoyaltyCouponItem;
  coupons: GenericMap<Coupon>;
}

interface IBuildOrderMetadataResult {
  metadata: IOrderMetadata;
  couponIds: string[];
  // Data needed for side-effect dispatches (only dispatch after confirming order will be sent)
  dispatchData: {
    cartLoyalties: ICartLoyalty[];
    couponIds: string[];
    couponObjList: Coupon[];
  };
}

const buildOrderMetadata = ({
  FEATURE_FLAG_LOYALTY_SUPPORT,
  cartLoyalties,
  customerName,
  sbSessionId,
  loyaltyCouponItem,
  coupons,
}: IBuildOrderMetadataParams): IBuildOrderMetadataResult => {
  const metadata: IOrderMetadata = {};

  // Apply 10 digit loyalty phone number
  if (FEATURE_FLAG_LOYALTY_SUPPORT && cartLoyalties.length) {
    metadata.loyalties = cartLoyalties.map(({ code }) => ({ code }));
  }

  // Add customer info if available
  if (customerName) {
    metadata.customer_info = {
      name: customerName,
    };
    logger.info({
      message: 'Customer info added to order',
      customerName,
      sessionId: sbSessionId,
    });
  }

  // Apply Loyalty Coupon Item
  if (loyaltyCouponItem && !loyaltyCouponItem.isApplied) {
    metadata.couponno = loyaltyCouponItem.couponno;
  }

  // Send coupon codes
  const couponIds = Object.keys(coupons);
  const couponObjList = couponIds.length ? Object.values(coupons) : [];

  if (couponIds.length) {
    metadata.coupons = couponObjList;
  }

  return {
    metadata,
    couponIds,
    dispatchData: {
      cartLoyalties:
        FEATURE_FLAG_LOYALTY_SUPPORT && cartLoyalties.length
          ? cartLoyalties
          : [],
      couponIds,
      couponObjList,
    },
  };
};

const buildAndDispatchOrderProcessingMetric = (
  state: IOrderState,
  requestId: string,
  dispatch: Dispatch
) => {
  const entry = state.requestsById[requestId];
  if (!entry?.orderEventId || !entry?.orderSentAt || !entry.checkResponse)
    return;

  const checkMsg = entry.checkResponse as CheckTransmissionMessage;
  const orderStatusMsg =
    entry.orderStatusResponse as IStatusTransmissionMessage | null;

  const orderSentAt = entry.orderSentAt;
  const orderStatusReceivedAt = orderStatusMsg?.timestamp ?? null;
  const checkReceivedAt = checkMsg.timestamp;

  const orderToOrderStatusMs = orderStatusReceivedAt
    ? new Date(orderStatusReceivedAt).getTime() -
      new Date(orderSentAt).getTime()
    : null;
  const orderStatusToCheckMs = orderStatusReceivedAt
    ? new Date(checkReceivedAt).getTime() -
      new Date(orderStatusReceivedAt).getTime()
    : null;
  const totalMs =
    new Date(checkReceivedAt).getTime() - new Date(orderSentAt).getTime();

  const isSuccess = checkMsg.data?.status === CheckResponseTypes.ok;

  dispatch(
    messagingActions.sendInfo({
      data: {
        type: 'ORDER_PROCESSING_METRIC',
        schema_version: '1.0',
        order_request_id: entry.orderEventId,
        order_status_id: orderStatusMsg?.id ?? null,
        check_id: checkMsg.id,
        timestamps: {
          order_sent_at: orderSentAt,
          order_status_received_at: orderStatusReceivedAt,
          check_received_at: checkReceivedAt,
        },
        durations_ms: {
          order_to_order_status_ms: orderToOrderStatusMs,
          order_status_to_check_ms: orderStatusToCheckMs,
          total_processing_ms: totalMs,
        },
        status: isSuccess ? 'SUCCESS' : 'FAILURE',
        failure_reason: isSuccess
          ? null
          : checkMsg.data?.error || checkMsg.data?.context || null,
        sla_threshold_ms: SLA_THRESHOLD_MS,
        sla_breached: totalMs > SLA_THRESHOLD_MS,
      } as any,
    })
  );
};

export {
  extractOrderId,
  initialOrderState,
  processOrderStatusAndCheckResponses,
  setLastOrderItemsFailed,
  populateMUOrderItems,
  buildMenuModuleItems,
  buildOrderMetadata,
  buildAndDispatchOrderProcessingMetric,
};
